// import classNames from 'classnames';
import classNames from 'classnames';
import { useId, useState, FocusEventHandler, useRef } from 'react';

import { useOnResize } from 'src/hooks/useOnResize';

import { debounce } from 'src/utils/debounce';

import type { PrimaryNavigationItem, LinkData } from 'src/types/components';

import ArrowDownIcon from 'assets/icons/arrow_down.svg';

import Link from 'components/BaseHelpers/Link';
import Text from 'components/BaseHelpers/Text';
import PrimarySubNavigation, {
  type Anchor,
} from 'components/Core/CoreNavigationHeader/components/DesktopPrimaryNav/PrimarySubNavigation';

import styles from './DesktopPrimaryNav.module.scss';

// also update const in _global-variables.scss
const HorizontalNavPadding = 40;

type PrimaryNavigationLinkProps = { page: LinkData; label: string };
const PrimaryNavigationLink = ({ page, label }: PrimaryNavigationLinkProps) => {
  const { href, target } = page;
  return (
    <li className={styles.menuItem}>
      <Link href={href} target={target} className={styles.label}>
        <Text field={label} />
      </Link>
    </li>
  );
};

type PrimaryNavigationMenuProps = PrimaryNavigationItem;
const PrimaryNavigationMenu = ({ label, subMenu }: PrimaryNavigationMenuProps) => {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<Anchor>('right');
  const dropdownLabel = useRef<null | HTMLButtonElement>(null);
  const subNav = useRef<null | HTMLDivElement>(null);
  const navId = useId();
  const { setOnResizeFn } = useOnResize();

  const calculateAnchor = debounce(() => {
    // reset nav to "natural" width
    setAnchor('right');
    requestAnimationFrame(() => {
      const screenWidth = window.innerWidth;
      // label left-hand position
      const labelLeftPosition = dropdownLabel.current?.getBoundingClientRect().left ?? 0;
      // label width
      const labelWidth = dropdownLabel.current?.getBoundingClientRect().width ?? 0;
      // subnav width
      const subNavWidth = subNav.current?.getBoundingClientRect().width ?? 0;

      let anchor: Anchor = 'right';

      // take the right-most part of the label, and see if anchoring there will extend beyond the left-side of the screen
      const canFitOnLeft = labelLeftPosition + labelWidth - subNavWidth - HorizontalNavPadding > 0;
      // take the left-most part of the label, and see if anchoring there will extend beyond the right-side of the screen
      const canFitOnRight = labelLeftPosition + subNavWidth + HorizontalNavPadding < screenWidth;

      // if there's space on the left, anchor to the right of the label
      if (canFitOnLeft) {
        anchor = 'right';
        // if there's space on the right, anchor to the left of the label
      } else if (canFitOnRight) {
        anchor = 'left';
        // if there isn't enough space on either side, anchor to the center (which should be left: nav-padding, right: nav-padding)
      } else {
        anchor = 'center';
      }

      setAnchor(anchor);
    });
  }, 200);

  setOnResizeFn(() => {
    // close the nav so it doesn't flash when re-calculating space
    setOpen(false);
    calculateAnchor();
  });

  if (!subMenu) {
    return;
  }

  const handleBlur: FocusEventHandler<HTMLElement> = (e) => {
    const target = e.currentTarget;

    requestAnimationFrame(() => {
      if (!target.contains(document.activeElement)) {
        closeMenu();
      }
    });
  };

  const openMenu = () => {
    setOpen(true);
  };

  const closeMenu = () => {
    setOpen(false);
  };

  const toggleMenu = () => {
    setOpen(!open);
  };

  return (
    <li
      onMouseOver={openMenu}
      onMouseLeave={closeMenu}
      className={classNames(styles.menuItem, styles[anchor])}
      onBlur={handleBlur}
    >
      <button
        id={`${navId}-trigger`}
        className={styles.label}
        aria-controls={navId}
        aria-expanded={open}
        onClick={toggleMenu}
        aria-label={`Submenu of ${label}`}
        ref={dropdownLabel}
      >
        <Text field={label} />
        <ArrowDownIcon aria-hidden className={styles.arrowIcon} />
      </button>
      <nav id={navId} aria-labelledby={`${navId}-trigger`}>
        <PrimarySubNavigation
          onClick={closeMenu}
          ref={subNav}
          className={classNames(styles.subNav, {
            [styles['subNav--open']]: open,
          })}
          {...subMenu}
          anchor={anchor}
        />
      </nav>
    </li>
  );
};

export type DesktopPrimaryNavProps = {
  navigationItems: PrimaryNavigationItem[];
};
const DesktopPrimaryNav = ({ navigationItems }: DesktopPrimaryNavProps): JSX.Element | null => {
  return (
    <ul className={styles.nav}>
      {navigationItems.map((item, index) => {
        const { itemType, label } = item;
        if (itemType === 'page' || itemType === 'external') {
          return <PrimaryNavigationLink page={item.page} label={label} key={index} />;
        }

        if (itemType === 'menu') {
          return <PrimaryNavigationMenu {...item} key={index} />;
        }

        return null;
      })}
    </ul>
  );
};

export default DesktopPrimaryNav;
